import { useState } from 'react';
import type { FutebolPartidaAtiva, FutebolJogoDia } from '@/shared/types';

export type ModoPlacar = 'pre_jogo' | 'ao_vivo' | 'encerrado';

function horarioBRT(iso: string): string {
  return new Date(iso).toLocaleTimeString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function EscudoGrande({ src, nome }: { src: string | null; nome: string }) {
  const [erro, setErro] = useState(false);
  if (!src || erro) {
    return (
      <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-white/10 text-sm font-bold uppercase text-white/40">
        {nome.slice(0, 2)}
      </div>
    );
  }
  return (
    <img src={src} alt={nome} className="h-14 w-14 shrink-0 object-contain" onError={() => setErro(true)} />
  );
}

function BadgeTempo({ tempo }: { tempo: string | null }) {
  if (!tempo) return null;
  if (tempo === 'HT') {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-md bg-amber-500/15 px-2 py-1 text-[11px] font-bold text-amber-400 ring-1 ring-amber-500/25">
        Intervalo
      </span>
    );
  }
  if (tempo === 'FT') {
    return (
      <span className="rounded-md bg-white/[0.06] px-2 py-1 text-[11px] font-medium text-white/45 ring-1 ring-white/[0.08]">
        Encerrado
      </span>
    );
  }
  if (tempo === 'ET') {
    return (
      <span className="rounded-md bg-orange-500/15 px-2 py-1 text-[11px] font-bold text-orange-400 ring-1 ring-orange-500/25">
        Prorrogação
      </span>
    );
  }
  if (tempo === 'PEN') {
    return (
      <span className="rounded-md bg-orange-500/15 px-2 py-1 text-[11px] font-bold text-orange-400 ring-1 ring-orange-500/25">
        Pênaltis
      </span>
    );
  }
  if (/^\d+$/.test(tempo)) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-md bg-rose-500/15 px-2 py-1 ring-1 ring-rose-500/30">
        <span className="h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-rose-400" />
        <span className="text-[11px] font-bold text-rose-400">No ar</span>
        <span className="font-mono text-[11px] font-bold tabular-nums text-rose-300/70">{tempo}'</span>
      </span>
    );
  }
  return <span className="font-mono text-[11px] text-white/45">{tempo}</span>;
}

function BadgeEmBreve({ horario }: { horario: string }) {
  return (
    <span className="rounded-md bg-orange-500/15 px-2 py-1 text-[11px] font-bold text-orange-400 ring-1 ring-orange-500/25">
      Em breve · {horarioBRT(horario)}
    </span>
  );
}

interface Props {
  partida: FutebolPartidaAtiva;
  jogo: FutebolJogoDia | null;
  modo: ModoPlacar;
}

/**
 * Placar de palco — âncora visual do hub Jogo.
 */
export function JogoPlacarHeader({ partida, jogo, modo }: Props) {
  const nomeCasa      = jogo?.time_casa      ?? '–';
  const nomeVisitante = jogo?.time_visitante ?? '–';
  const aoVivo = modo === 'ao_vivo' && !!partida.tempo_jogo && /^\d+$/.test(partida.tempo_jogo);

  return (
    <div className="relative shrink-0 overflow-hidden border-b border-white/[0.05] bg-white/[0.02] px-6 py-4">
      {aoVivo && (
        <span className="absolute inset-y-0 left-0 w-0.5 bg-rose-400" aria-hidden />
      )}
      {modo === 'encerrado' && (
        <span className="absolute inset-y-0 left-0 w-0.5 bg-white/20" aria-hidden />
      )}
      {modo === 'pre_jogo' && (
        <span className="absolute inset-y-0 left-0 w-0.5 bg-orange-400" aria-hidden />
      )}

      <div className="flex items-center justify-center gap-5">
        <div className="flex min-w-0 flex-1 flex-col items-center gap-1.5">
          <EscudoGrande src={jogo?.escudo_casa ?? null} nome={nomeCasa} />
          <span className="w-full truncate text-center text-xs font-semibold leading-tight text-white/90">
            {nomeCasa}
          </span>
        </div>

        <div className="flex shrink-0 flex-col items-center gap-2">
          <div className="flex items-center gap-2.5">
            <span className="font-mono text-4xl font-black tabular-nums text-white">{partida.placar_casa}</span>
            <span className="text-2xl font-light text-white/30">·</span>
            <span className="font-mono text-4xl font-black tabular-nums text-white">{partida.placar_visitante}</span>
          </div>
          {modo === 'pre_jogo' && jogo?.horario ? (
            <BadgeEmBreve horario={jogo.horario} />
          ) : modo === 'encerrado' ? (
            <BadgeTempo tempo="FT" />
          ) : (
            <BadgeTempo tempo={partida.tempo_jogo} />
          )}
        </div>

        <div className="flex min-w-0 flex-1 flex-col items-center gap-1.5">
          <EscudoGrande src={jogo?.escudo_visitante ?? null} nome={nomeVisitante} />
          <span className="w-full truncate text-center text-xs font-semibold leading-tight text-white/90">
            {nomeVisitante}
          </span>
        </div>
      </div>

      {jogo?.liga_nome && (
        <div className="mt-3 flex items-center gap-2">
          <div className="h-px flex-1 bg-white/[0.05]" />
          <span className="shrink-0 px-1 text-[11px] font-medium text-white/35">
            {jogo.liga_nome}
          </span>
          <div className="h-px flex-1 bg-white/[0.05]" />
        </div>
      )}
    </div>
  );
}
